/**
 * FitaBordaSimulador — retângulo SVG proporcional da peça com as 4 bordas
 * tocáveis para marcar onde vai fita. Comprimento SEMPRE no eixo VERTICAL
 * (sentido do veio da chapa); hachura + seta indicam o veio.
 *
 * REGRA GEOMÉTRICA (não inverter — bug 2026-07-07):
 * O comprimento (veio) é o eixo VERTICAL. Logo:
 *   - bordas VERTICAIS (esquerda/direita) correm no sentido do COMPRIMENTO
 *     → acionam fita_frente / fita_traseira → colunas BORDA_FACE_FRENTE/TRASEIRA
 *   - bordas HORIZONTAIS (cima/baixo) correm no sentido da LARGURA
 *     → acionam fita_le / fita_ld → colunas BORDA_FACE_LE / BORDA_FACE_LD
 * A exportação (solicitacoes/xls.py) mapeia campo→coluna 1:1; é AQUI que a
 * borda física precisa casar com o campo certo.
 *
 * Props:
 * - `comprimento`, `largura` (strings do form, aceitam vírgula)
 * - `bordas`: { fita_traseira, fita_frente, fita_le, fita_ld } booleans
 * - `onToggleBorda(campo)`
 * - `onDefinirTodas(boolean)`
 */
import React from "react";

import { LADOS_PECA, contarBordasComFita, parseDecimal } from "./utils";
import { ChipBtn, ChipsLinha, FitaContador, FitaPlaceholder, FitaWrap } from "./styles";

const ALTURA_MAX = 230;
const ALTURA_MIN = 90;
const LARGURA_MAX = 240;
const LARGURA_MIN = 56;
const TOQUE = 44; // área mínima de toque por borda

function calcularDimensoes(comprimento, largura) {
  let alt = ALTURA_MAX;
  let larg = alt * (largura / comprimento);
  if (larg > LARGURA_MAX) {
    larg = LARGURA_MAX;
    alt = larg * (comprimento / largura);
  }
  if (alt < ALTURA_MIN) alt = ALTURA_MIN;
  if (larg < LARGURA_MIN) larg = LARGURA_MIN;
  return { alt: Math.round(alt), larg: Math.round(larg) };
}

function estiloBorda(ativa) {
  return ativa
    ? { stroke: "var(--accent)", strokeWidth: 7, strokeDasharray: "none" }
    : { stroke: "var(--ink-3)", strokeWidth: 2, strokeDasharray: "7 5" };
}

export default function FitaBordaSimulador({
  comprimento,
  largura,
  bordas,
  onToggleBorda,
  onDefinirTodas,
}) {
  const c = parseDecimal(comprimento);
  const l = parseDecimal(largura);
  const total = contarBordasComFita(bordas);

  if (!c || !l || c <= 0 || l <= 0) {
    return (
      <FitaPlaceholder>
        Digite comprimento e largura para ver a peça
      </FitaPlaceholder>
    );
  }

  const { alt, larg } = calcularDimensoes(c, l);
  const margem = 52; // espaço para rótulos + área de toque
  const vw = larg + margem * 2;
  const vh = alt + margem * 2;
  const x0 = margem;
  const y0 = margem;
  const x1 = margem + larg;
  const y1 = margem + alt;
  const cx = x0 + larg / 2;
  const cy = y0 + alt / 2;

  const hachuras = [];
  for (let hx = x0 + 12; hx < x1 - 4; hx += 14) {
    hachuras.push(hx);
  }

  // Geometria por LADO físico (topo/base = horizontais; esq/dir = verticais).
  // O mapa lado→campo vem de LADOS_PECA (fonte única — regra do veio).
  const geomPorLado = {
    topo: {
      linha: { x1: x0, y1: y0, x2: x1, y2: y0 },
      toque: { x: x0 - TOQUE / 2, y: y0 - TOQUE / 2, width: larg + TOQUE, height: TOQUE },
      texto: { x: cx, y: y0 - 16, rot: 0 },
    },
    base: {
      linha: { x1: x0, y1, x2: x1, y2: y1 },
      toque: { x: x0 - TOQUE / 2, y: y1 - TOQUE / 2, width: larg + TOQUE, height: TOQUE },
      texto: { x: cx, y: y1 + 26, rot: 0 },
    },
    esq: {
      linha: { x1: x0, y1: y0, x2: x0, y2: y1 },
      toque: { x: x0 - TOQUE / 2, y: y0 + TOQUE / 2, width: TOQUE, height: Math.max(alt - TOQUE, 8) },
      texto: { x: x0 - 18, y: cy, rot: -90 },
    },
    dir: {
      linha: { x1, y1: y0, x2: x1, y2: y1 },
      toque: { x: x1 - TOQUE / 2, y: y0 + TOQUE / 2, width: TOQUE, height: Math.max(alt - TOQUE, 8) },
      texto: { x: x1 + 18, y: cy, rot: 90 },
    },
  };
  const definicaoBordas = LADOS_PECA.map(({ lado, campo, rotulo }) => ({
    campo,
    rotulo,
    ...geomPorLado[lado],
  }));

  return (
    <FitaWrap>
      <svg
        viewBox={`0 0 ${vw} ${vh}`}
        width="100%"
        style={{ maxWidth: vw, display: "block" }}
        role="group"
        aria-label="Simulador de fita de borda"
      >
        {/* corpo da peça */}
        <rect
          x={x0}
          y={y0}
          width={larg}
          height={alt}
          fill="var(--accent-soft)"
          stroke="none"
          rx="2"
        />
        {/* hachura vertical = sentido do veio */}
        {hachuras.map((hx) => (
          <line
            key={hx}
            x1={hx}
            y1={y0 + 6}
            x2={hx}
            y2={y1 - 6}
            stroke="var(--info-border)"
            strokeWidth="1"
          />
        ))}
        {/* seta dupla do veio + rótulo */}
        <line
          x1={cx}
          y1={y0 + 14}
          x2={cx}
          y2={y1 - 14}
          stroke="var(--info-text)"
          strokeWidth="1.6"
          markerStart="url(#setaVeioCima)"
          markerEnd="url(#setaVeioBaixo)"
        />
        <defs>
          <marker id="setaVeioCima" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto-start-reverse">
            <path d="M0,8 L4,0 L8,8" fill="none" stroke="var(--info-text)" strokeWidth="1.4" />
          </marker>
          <marker id="setaVeioBaixo" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto-start-reverse">
            <path d="M0,8 L4,0 L8,8" fill="none" stroke="var(--info-text)" strokeWidth="1.4" />
          </marker>
        </defs>
        <text
          x={cx + 9}
          y={cy}
          fill="var(--info-text)"
          fontSize="11"
          fontWeight="700"
          dominantBaseline="middle"
        >
          veio
        </text>

        {/* bordas: linha visível + rótulo + área de toque transparente */}
        {definicaoBordas.map(({ campo, rotulo, linha, toque, texto }) => {
          const ativa = !!bordas[campo];
          return (
            <g key={campo}>
              <line {...linha} strokeLinecap="round" {...estiloBorda(ativa)} />
              <text
                x={texto.x}
                y={texto.y}
                fill={ativa ? "var(--accent)" : "var(--ink-2)"}
                fontSize="10.5"
                fontWeight={ativa ? "700" : "500"}
                textAnchor="middle"
                dominantBaseline="middle"
                transform={texto.rot ? `rotate(${texto.rot} ${texto.x} ${texto.y})` : undefined}
              >
                {rotulo}
              </text>
              <rect
                x={toque.x}
                y={toque.y}
                width={toque.width}
                height={toque.height}
                fill="transparent"
                style={{ cursor: "pointer" }}
                role="button"
                aria-pressed={ativa}
                aria-label={`Fita na borda ${rotulo.toLowerCase()}`}
                onClick={() => onToggleBorda(campo)}
              />
            </g>
          );
        })}
      </svg>

      <ChipsLinha>
        <ChipBtn type="button" onClick={() => onDefinirTodas(true)}>Todas</ChipBtn>
        <ChipBtn type="button" onClick={() => onDefinirTodas(false)}>Nenhuma</ChipBtn>
        <FitaContador>{total} de 4 bordas com fita</FitaContador>
      </ChipsLinha>
    </FitaWrap>
  );
}
